import React from "react";
import {Navigate, Outlet, Route, Routes, useLocation} from "react-router-dom";
import {ResponsiveNavTabs, ResponsiveNavTab} from "../ResponsiveNavTabs";
import {Login, Logout, ForgotPassword, ReSendEmail, ResetPassword, Signup, VerifyEmail, Profile, Users, Address, EditUser} from "./index";
import {IconTab} from "../_components";
import {useAuth} from "../../context";
import {useBasePath} from "../../hooks";
import {AddEditAddress} from "./address/AddEditAddress";
import {Carriers} from "./admin/carrier/Carriers";
import {Countries} from "./admin/country/Countries";
import {AddEditCountry} from "./admin/country/AddEditCountry";
import {AddEditCarrier} from "./admin/carrier/AddEditCarrier";
import {Medias} from "./admin/media/Medias";
import {AddEditMedia} from "./admin/media/AddEditMedia";
import {Tags} from "./admin/tag/Tags";
import {AddEditTags} from "./admin/tag/AddEditTag";
import {AddEditProduct} from "./admin/product/AddEditProduct";
import {Products} from "./admin/product/Products";
import {AddEditUser} from "./users/AddEditUser";
import {Orders} from "./order/Orders";
import {OrderView} from "./order/OrderView";
import {OrderEdit} from "./order/OrderEdit";
import {Dispatch} from "./order/Dispatch";
import {Tips} from "./admin/tip/Tips";
import {AddEditTip} from "./admin/tip/AddEditTip";
import {useBrowserTitle} from "../../hooks/useBrowserTitle";
import {Subscriptions} from "./subscription/Subscriptions";
import {SubscriptionView} from "./subscription/SubscriptionView";
import {Announcement} from "./announcement/Announcement";
import {AddEditAnnouncement} from "./announcement/AddEditAnnoucement";
import {Discount} from "./discount/Discount";
import {AddEditDiscount} from "./discount/AddEditDiscount";

export function RoutesBackend() {
    const {admin, userId} = useAuth()
    const path = useBasePath(1)

    return <Routes>
        {/* Profile and Devices */}
        <Route element={
            <ResponsiveNavTabs>
                <ResponsiveNavTab to={`${path}/profile`} icon={<IconTab name="person"/>} title="Profile"/>
                <ResponsiveNavTab to={`${path}/address/${userId}`} icon={<IconTab name="geo-alt"/>} title="Address"/>
                <ResponsiveNavTab to={`${path}/order/${userId}`} icon={<IconTab name="box"/>} title="Orders"/>
                <ResponsiveNavTab to={`${path}/subscription/${userId}`} icon={<IconTab name="award"/>} title="Subscription"/>
                <ResponsiveNavTab to={`${path}/announcement/${userId}`} icon={<IconTab name="megaphone"/>} title="Announcement"/>

                {admin && <ResponsiveNavTab to={`${path}/user`} icon={<IconTab name="people"/>} title="Users" variant="info"/>}
                {admin && <ResponsiveNavTab to={`${path}/country`} icon={<IconTab name="globe"/>} title="Country" variant="info"/>}
                {admin && <ResponsiveNavTab to={`${path}/carrier`} icon={<IconTab name="truck"/>} title="Carrier" variant="info"/>}
                {admin && <ResponsiveNavTab to={`${path}/tip`} icon={<IconTab name="chat-square-text"/>} title="Tips" variant="info"/>}
                {admin && <ResponsiveNavTab to={`${path}/tag`} icon={<IconTab name="bookmarks"/>} title="Tags" variant="info"/>}
                {admin && <ResponsiveNavTab to={`${path}/media`} icon={<IconTab name="file-image"/>} title="Media" variant="info"/>}
                {admin && <ResponsiveNavTab to={`${path}/product`} icon={<IconTab name="file-richtext"/>} title="Product" variant="info"/>}
                {admin && <ResponsiveNavTab to={`${path}/dispatch`} icon={<IconTab name="send"/>} title="Dispatch" variant="info"/>}
                {admin && <ResponsiveNavTab to={`${path}/discount`} icon={<IconTab name="percent"/>} title="Discount" variant="info"/>}
            </ResponsiveNavTabs>}>

            {/* User's allowed paths */}
            <Route path="profile" element={<PrivatePath><Profile/></PrivatePath>}/>
            <Route path="address/:uid" element={<PrivatePath><Address/></PrivatePath>}/>
            <Route path="address/:uid/:aid" element={<PrivatePath><AddEditAddress/></PrivatePath>}/>
            <Route path="order/:uid" element={<PrivatePath><Orders/></PrivatePath>}/>
            <Route path="order/:uid/:oid" element={<PrivatePath><OrderView/></PrivatePath>}/>
            <Route path="subscription/:uid" element={<PrivatePath><Subscriptions/></PrivatePath>}/>
            <Route path="subscription/:uid/:sid" element={<PrivatePath><SubscriptionView/></PrivatePath>}/>
            <Route path="announcement/:uid" element={<PrivatePath><Announcement/></PrivatePath>}/>
            <Route path="announcement/:uid/:aid" element={<PrivatePath><AddEditAnnouncement/></PrivatePath>}/>

            {/* Admin allowed paths */}
            <Route element={<AdminGroupedPaths/>}>
                <Route path="user" element={<AdminPath><Users/></AdminPath>}/>
                <Route path="user/:uid" element={<AdminPath><AddEditUser/></AdminPath>}/>
                <Route path="country" element={<AdminPath><Countries/></AdminPath>}/>
                <Route path="country/:coid" element={<AdminPath><AddEditCountry/></AdminPath>}/>
                <Route path="carrier" element={<AdminPath><Carriers/></AdminPath>}/>
                <Route path="carrier/:caid" element={<AdminPath><AddEditCarrier/></AdminPath>}/>
                <Route path="tip" element={<AdminPath><Tips/></AdminPath>}/>
                <Route path="tip/:tiid" element={<AdminPath><AddEditTip/></AdminPath>}/>
                <Route path="tag" element={<AdminPath><Tags/></AdminPath>}/>
                <Route path="tag/:tid" element={<AdminPath><AddEditTags/></AdminPath>}/>
                <Route path="media" element={<AdminPath><Medias/></AdminPath>}/>
                <Route path="media/:mid" element={<AdminPath><AddEditMedia/></AdminPath>}/>
                <Route path="product" element={<AdminPath><Products/></AdminPath>}/>
                <Route path="product/:pid" element={<AdminPath><AddEditProduct/></AdminPath>}/>
                <Route path="dispatch" element={<AdminPath><Dispatch/></AdminPath>}/>
                <Route path="dispatch/:uid/:oid" element={<AdminPath><OrderEdit/></AdminPath>}/>
                <Route path="discount" element={<AdminPath><Discount/></AdminPath>}/>
                <Route path="discount/:did" element={<AdminPath><AddEditDiscount/></AdminPath>}/>
            </Route>
        </Route>

        <Route path="*" element={<Navigate to="/backend/profile" replace={true}/>}/>
    </Routes>
}


function PrivatePath({children}) {
    const {loggedIn} = useAuth()
    const location = useLocation()

    if (!loggedIn)
        return <Navigate to="/account/login" state={{from: location}} replace={true}/>
    return children
}

function AdminPath({children}) {
    const {loggedIn, admin} = useAuth()
    const location = useLocation()

    if (!loggedIn)
        return <Navigate to="/account/login" state={{from: location}} replace={true}/>

    if (!admin)
        return <Navigate to="/backend/profile" replace={true}/>

    return children
}

function AdminGroupedPaths() {
    useBrowserTitle("Admin")
    return <Outlet/>
}